import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useCallback, useMemo, useState } from 'react';
import type { ModalListItem } from 'src/components/ModalContainer';
import { SelectSiteDialog } from 'src/components/SelectSiteDialog/select-site-dialog';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useConversationParticipants } from 'src/graphql';
import { Address } from 'src/graphql/entities/address/address';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { DEFAULT_PAGINATION_LIMIT, useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { sortAlphabetically } from 'src/utils/sorting';

export const UpdateConversationToAddressModal = () => {
  const { closeModal } = useModalMethods(Modals.UpdateConversationToAddress);

  const [searchValue, setSearchValue] = useState('');

  const { isOpened, modalProps } = useModalState(Modals.UpdateConversationToAddress);
  const { conversation, address, onSelect } = modalProps ?? {};

  const initialRequester = conversation?.requesterData;

  const { conversationParticipations: participations } = useConversationParticipationRecords({
    conversationId: conversation?.id || '',
    skip: !!conversation?.associatedAddresses.length || !!initialRequester,
  });

  const queryParams = useMemo(() => {
    return {
      participantIds: participations?.map(
        (participation) => participation.channel.conversationParticipantId,
      ),
      filter: [
        {
          attribute: 'participantEntity',
          operator: '=',
          value: ConversationParticipantEntity.Client,
        },
      ],
    };
  }, [participations]);

  const { conversationParticipants } = useConversationParticipants(queryParams, {
    skip: !participations?.length,
  });

  const clientIds = useMemo(
    () =>
      initialRequester
        ? [initialRequester.participantIdentity]
        : conversationParticipants?.map((participant) => participant.participantIdentity),
    [conversationParticipants, initialRequester],
  );

  const filters = useMemo<ConditionInput[]>(
    () => [
      {
        attribute: 'clients/clientId',
        operator: 'in',
        value: Array.isArray(clientIds) ? clientIds.toSorted().join(',') : clientIds,
      },
    ],
    [clientIds],
  );

  const { records: addresses, isLoading: loadingAddresses } =
    useSearch({
      entityName: EntityNames.Site,
      searchTerm: searchValue,
      showResultsWhenEmptySearchTerm: true,
      filter: filters,
      limit: DEFAULT_PAGINATION_LIMIT * 10,
    }) ?? {};

  const clientAddresses = useMemo(
    () => (addresses ? Object.values(addresses) : []),
    [addresses],
  );

  const sitesToAddFilter = useMemo(() => {
    const initialFilters = [
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: [conversation?.partnerId, PLATFORM_PARTNER_ID].join(','),
      },
    ];

    return [
      {
        attribute: 'addressId',
        operator: 'not_in',
        value: clientAddresses
          .map((site) => site.addressId)
          .concat([conversation?.address?.addressId ?? address?.addressId ?? ''])
          .join(','),
      },
      ...initialFilters,
    ];
  }, [
    clientAddresses,
    conversation?.address?.addressId,
    conversation?.partnerId,
    address?.addressId,
  ]);

  const { records: sitesToAdd, isLoading: loadingSitesToAdd } =
    useSearch({
      entityName: EntityNames.Site,
      searchTerm: searchValue,
      skip: !searchValue,
      filter: sitesToAddFilter,
    }) ?? {};

  const filteredSitesToAdd = useMemo(
    () =>
      sitesToAdd
        // Remove duplicated elements
        ?.filter(
          (site, index, arr) => arr.findIndex((s) => s.addressId === site.addressId) === index,
        )
        .map((site) => ({
          label: site.fullAddress ?? '',
          ...site,
        })) ?? [],
    [sitesToAdd],
  );

  const filteredOptions = useMemo<ModalListItem[]>(() => {
    const others = clientAddresses
      .filter((item) => item.addressId !== address?.addressId)
      .filter((item) =>
        searchValue.trim() === ''
          ? true
          : item.fullAddress?.toLowerCase().includes(searchValue.toLowerCase()),
      )
      .map((address) => ({
        id: address.addressId,
        title: address.fullAddress ?? '',
        group: 'Other sites',
      }))
      .reduce<{ id: string; title: string; group: string }[]>((acc, curr) => {
        const exists = acc.find((item) => item.title === curr.title);
        if (!exists) acc.push(curr);
        return acc;
      }, [])
      .sort(sortAlphabetically('title'));

    const suggestions = filteredSitesToAdd.map((address) => ({
      id: address.addressId,
      title: address.fullAddress ?? '',
      group: 'Suggestions',
    }));

    return [
      ...(conversation?.address?.addressId || address?.addressId
        ? [
            {
              id: conversation?.address?.addressId ?? address!.addressId,
              title: conversation?.address?.fullAddress ?? (address?.fullAddress || ''),
              group: 'Current site',
            },
          ]
        : []),
      ...suggestions,
      ...others,
    ];
  }, [
    clientAddresses,
    filteredSitesToAdd,
    conversation?.address?.addressId,
    conversation?.address?.fullAddress,
    address,
    searchValue,
  ]);

  const onItemSelect = useCallback(
    (item: ModalListItem, createdSite?: Address) => {
      closeModal();

      const addressId = item.id;
      const clientAddress = clientAddresses.find((item) => item.addressId === addressId);
      const siteToAdd =
        createdSite ?? filteredSitesToAdd.find((item) => item.addressId === addressId);

      onSelect?.(item.id, {
        // think of refactor and reduce number of arguments
        clientAddress,
        siteToAdd,
        filters,
      });
    },
    [clientAddresses, closeModal, filteredSitesToAdd, filters, onSelect],
  );

  const loading = loadingAddresses || loadingSitesToAdd;

  return (
    <SelectSiteDialog
      open={isOpened}
      title="Change site"
      testId="change-conversation-site-modal"
      loading={loading}
      filters={filters}
      options={filteredOptions}
      onSelect={onItemSelect}
      partnerId={conversation?.partnerId}
      onClose={closeModal}
      onSearchChange={setSearchValue}
    />
  );
};
